"use client";

import { createContext, useContext, useState, useMemo, useCallback, ReactNode } from "react";
import type { Notification, ActiveNotification } from "@/types/notification";

interface NotificationContextType {
  notifications: Notification[];
  setNotifications: (notifications: Notification[]) => void;
  activeNotification: ActiveNotification | null;
  setActiveNotification: (notification: ActiveNotification | null) => void;
  markAsRead: (notificationId: string) => void;
  linkSessionToNotification: (notificationId: string, sessionId: string) => void;
  getNotificationIdForSession: (sessionId: string) => string | null;
  unreadCount: number;
  loaded: boolean;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotificationsState] = useState<Notification[]>([]);
  const [activeNotification, setActiveNotification] = useState<ActiveNotification | null>(null);
  const [loaded, setLoaded] = useState(false);

  const setNotifications = useCallback((notifications: Notification[]) => {
    setNotificationsState(notifications);
    setLoaded(true);
  }, []);

  const markAsRead = useCallback((notificationId: string) => {
    setNotificationsState(prev =>
      prev.map(n =>
        n.notification_id === notificationId ? { ...n, read: true } : n
      )
    );
  }, []);

  const linkSessionToNotification = useCallback((notificationId: string, sessionId: string) => {
    setNotificationsState(prev =>
      prev.map(n =>
        n.notification_id === notificationId ? { ...n, session_id: sessionId } : n
      )
    );
  }, []);

  const getNotificationIdForSession = useCallback((sessionId: string): string | null => {
    const notification = notifications.find(n => n.session_id === sessionId);
    if (notification) return notification.notification_id;
    return null;
  }, [notifications]);

  const unreadCount = useMemo(
    () => notifications.filter(n => !n.read).length,
    [notifications]
  );

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        setNotifications,
        activeNotification,
        setActiveNotification,
        markAsRead,
        linkSessionToNotification,
        getNotificationIdForSession,
        unreadCount,
        loaded,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotifications must be used within NotificationProvider");
  }
  return context;
}
